import { Heading5 } from '@blueprint/ui';
import { ArrowRightSmall } from '@blueprint/icons';
import { ListingImage } from '@zoopla/listings/image';
import { ListingFeatureList } from '@zoopla/listings/features';
import {
    HeadingRow,
    StyledCenteringDiv
} from './SavedListingsWidgetMobile.styles';
import {
    trackClickSavedListingWidgetCard,
    trackClickSeeAllSavedListings
} from '../SavedListingsWidget.tracking';
import {
    TriggerWhenVisible,
    ListingCardMiniColumnWithShrinkBreakpoint,
    ListingCardMini,
    StyledListingCardMiniTitle,
    StyledListingCardMiniDetails,
    StyledListingCardMiniPrice,
    ListingCardMiniSkeleton,
    BadgeContainerMini
} from '@zoopla/listings/cards';
import { useListing } from '../graphql/Listing/useListing';
import { MappedEnrichedSavedPropertyId } from '../graphql/EnrichedSavedPropertyIds/useEnrichedSavedPropertyIds';
import { getBadgeForSavedListing } from '@zoopla/listing-badges';
import { Badge } from '@zoopla-eng/zds-components/badges';
import { ButtonRight } from '@zoopla-eng/zds-components/button';

const SavedListingCardMobile = ({
    listingId,
    isTriggered
}: {
    listingId: string;
    isTriggered: boolean;
}) => {
    const listing = useListing(listingId, !isTriggered);

    if (!listing) {
        return <ListingCardMiniSkeleton />;
    }

    const {
        imagePreview,
        featurePreview,
        displayAddress,
        listingLinkHref,
        displayPrice,
        shortDisplayAddress,
        publicationStatus,
        section,
        statusSummary,
        tags
    } = listing;

    const badgeProps = getBadgeForSavedListing({
        isExpired: publicationStatus === 'Expired',
        isNewBuild: section === 'new-homes',
        statusSummaryLabel: statusSummary?.label,
        tagLabels: tags?.map(({ label }) => label)
    });

    return (
        <ListingCardMini
            testId="saved-listing-card-mobile"
            listingLinkHref={listingLinkHref}
            trackClick={trackClickSavedListingWidgetCard}
        >
            {badgeProps ? (
                <BadgeContainerMini>
                    <Badge {...badgeProps} />
                </BadgeContainerMini>
            ) : null}
            <ListingImage image={imagePreview} useImageLazyLoad={true} />
            <StyledListingCardMiniDetails
                data-testid={`mini-saved-property-card-details-${listingId}`}
            >
                <StyledListingCardMiniTitle>
                    {shortDisplayAddress || displayAddress}
                </StyledListingCardMiniTitle>
                <StyledListingCardMiniPrice>
                    {displayPrice}
                </StyledListingCardMiniPrice>
                <ListingFeatureList features={featurePreview} />
            </StyledListingCardMiniDetails>
        </ListingCardMini>
    );
};

export const SavedListingsWidgetMobile = ({
    listings
}: {
    listings: Array<MappedEnrichedSavedPropertyId>;
}) => {
    return (
        <div
            data-testid="saved-property-widget"
            aria-labelledby="saved-properties-widget-header"
        >
            <HeadingRow>
                <Heading5 size="6" id="saved-properties-widget-header">
                    Recently saved properties
                </Heading5>
            </HeadingRow>

            <ListingCardMiniColumnWithShrinkBreakpoint>
                {listings.map(({ listingId }) => {
                    return (
                        <TriggerWhenVisible triggerDelay={100} key={listingId}>
                            {(isTriggered: boolean) => {
                                return (
                                    <SavedListingCardMobile
                                        listingId={listingId}
                                        isTriggered={isTriggered}
                                    />
                                );
                            }}
                        </TriggerWhenVisible>
                    );
                })}
            </ListingCardMiniColumnWithShrinkBreakpoint>

            <StyledCenteringDiv>
                <ButtonRight
                    category="tertiary"
                    icon={<ArrowRightSmall />}
                    data-testid="see-all-saved-properties"
                    onClick={trackClickSeeAllSavedListings}
                    {...{ as: 'a', href: '/favourites/properties/' }}
                >
                    See all saved properties
                </ButtonRight>
            </StyledCenteringDiv>
        </div>
    );
};
